import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { pct } from "../lib/ui";
import {
  Siren, Percent, ClipboardList, Flag, ListTodo, CalendarCheck2, CheckCircle2,
  TestTube2, FileClock, ArrowRight,
} from "lucide-react";

const Tile = ({ icon: Icon, label, value, tone = "slate", onClick, testid, cta }) => {
  const tones = {
    orange: "text-[#F05A28]",
    blue: "text-[#0284C7]",
    amber: "text-[#B45309]",
    red: "text-[#B91C1C]",
    green: "text-[#047857]",
    slate: "text-[#1E293B] bg-slate-100",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      data-testid={testid}
      className={`text-left bg-white border border-slate-200 rounded-lg p-4 flex items-center gap-3 transition-colors ${onClick ? "hover:border-slate-400 cursor-pointer" : "cursor-default"}`}
    >
      <div className={`h-8 w-8 rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0 ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 truncate">{label}</p>
        <p className="text-xl font-semibold text-slate-900 leading-tight">{value}</p>
      </div>
      {cta && onClick && <ArrowRight className="h-4 w-4 text-slate-300 ml-auto" />}
    </button>
  );
};

export default function MyTasks() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["my-tasks"], queryFn: async () => (await api.get("/my-tasks")).data });
  if (!data) return null;

  const role = user?.role;
  const newRoute = role === "lab_tech" ? "/lab/new"
    : (user?.pillar && user.pillar !== "surveillance" && user.pillar !== "laboratory") ? `/pillar/${user.pillar}/new`
    : "/new-report";
  const isSurv = role === "field_epi" || data.alerts_to_investigate !== undefined;

  const tiles = [];

  // Daily/submission CTA for field-type agents
  if (role !== "admin" && role !== "program_manager" && role !== "si_analyst") {
    tiles.push(
      <Tile key="today" testid="mytask-today"
        icon={data.submitted_today ? CheckCircle2 : CalendarCheck2}
        label={t("Rapport du jour")}
        value={data.submitted_today ? t("Soumis") : t("À soumettre")}
        tone={data.submitted_today ? "green" : "orange"}
        cta={!data.submitted_today}
        onClick={data.submitted_today ? undefined : () => navigate(newRoute)} />
    );
  }

  if (data.alerts_to_investigate !== undefined) {
    tiles.push(
      <Tile key="alerts" testid="mytask-alerts" icon={Siren}
        label={t("Alertes à investiguer")} value={data.alerts_to_investigate}
        tone={data.alerts_to_investigate > 0 ? "red" : "slate"} cta
        onClick={() => navigate("/signals")} />
    );
  }

  if (data.completeness !== undefined) {
    tiles.push(
      <Tile key="comp" testid="mytask-completeness" icon={Percent}
        label={`${t("Complétude")} · ${data.zone}`} value={pct(data.completeness)}
        tone={data.completeness !== null && data.completeness < 80 ? "amber" : "blue"} />
    );
  }

  if (data.flagged_reports !== undefined && data.flagged_reports > 0) {
    tiles.push(
      <Tile key="flagged" testid="mytask-flagged" icon={Flag}
        label={t("Rapports signalés")} value={data.flagged_reports} tone="red" cta
        onClick={() => navigate("/reports")} />
    );
  }

  if (data.pending_recommendations !== undefined) {
    tiles.push(
      <Tile key="recs" testid="mytask-recs" icon={ListTodo}
        label={t("Recommandations en attente")} value={data.pending_recommendations}
        tone={data.pending_recommendations > 0 ? "amber" : "slate"} cta
        onClick={() => navigate("/recommendations")} />
    );
  }

  if (role === "lab_tech") {
    tiles.push(
      <Tile key="samples" testid="mytask-samples" icon={TestTube2}
        label={t("Échantillons en attente")} value={data.samples_pending_week ?? 0}
        tone={(data.samples_pending_week ?? 0) > 0 ? "amber" : "blue"} />
    );
  }

  tiles.push(
    <Tile key="week" testid="mytask-week" icon={ClipboardList}
      label={t("Mes rapports (semaine)")} value={data.my_reports_this_week ?? 0} tone="slate" />
  );

  return (
    <section className="mb-6" data-testid="my-tasks-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="eyebrow mb-1">{t("Mon espace")}</p>
          <h2 className="text-lg font-bold text-slate-900">{t("Mes tâches du moment")}</h2>
        </div>
        <span className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500">
          <FileClock className="h-3.5 w-3.5" />
          {t("Semaine épidémiologique")} {data.epi_week} · {data.last_report_date ? `${t("dernier rapport")}: ${data.last_report_date}` : t("aucun rapport récent")}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">{tiles}</div>
    </section>
  );
}
